import GoogleAnalyticsEvents from '../../../common/GoogleAnalyticsEvents';
import { FeatureToggle, isFeatureEnabled } from '../../../util/featureToggle';
import GTMViewItemModel from '../../../gtm/models/GTMViewItemModel';
import GTM from '../../../gtm/GTM';
import GTMDetailAddToCartButtonClickModel from '../../../gtm/models/GTMDetailAddToCartButtonClickModel';

export interface HouseDetailDataLayerData {
    custom_lastcountry: string;
    custom_lastregion: string;
    custom_lastdepartment: string;
    custom_lastcity: string;
    custom_bedrooms: number;
    custom_type: string;
    custom_theme: string;
    custom_service: string;
    custom_location: string | null;
    custom_durability: string;
    custom_reviews: number;
    custom_nature: number;
    currency: string;
    products: dataLayerEcommerceProduct[];
}

interface PriceStructure {
    price: number;
    basePrice: number;
    discount: number;
    pricePerNight: number;
}

export default class DetailFormTracking {
    readonly #formElement: HTMLFormElement;

    constructor(form: HTMLFormElement) {
        this.#formElement = form;
    }

    public static getHouseDetailDataLayerData(): HouseDetailDataLayerData | undefined {
        const dataLayerElement = document.getElementById('data-layer');
        const dataLayerContent = dataLayerElement?.getAttribute('content');

        if (!dataLayerElement || !dataLayerContent) {
            return undefined;
        }

        try {
            return JSON.parse(dataLayerContent) as HouseDetailDataLayerData;
        } catch {
            return undefined;
        }
    }

    public static getInstantBookingValue(): boolean {
        const instantBookingElement = document.querySelector<HTMLMetaElement>(
            'meta[id="instant-booking"]'
        );

        return instantBookingElement?.getAttribute('content') === 'true';
    }

    public trackDetailPageImpressionSqueezely(
        companionData: TravelPartyDataInterface,
        dateSelection: DateRangeString,
        destination: string
    ): void {
        const dataLayerElement = document.getElementById('data-layer');

        if (!dataLayerElement) {
            return;
        }

        let initialData = {};
        if (dataLayerElement.hasAttribute('content')) {
            initialData = JSON.parse(dataLayerElement.getAttribute('content') ?? '');
        }

        const priceStructure = this.#getPriceStructure();

        const data: GoogleAnalytics4ViewContentEvent = {
            ...initialData,
            custom_adults: companionData.adults,
            custom_children: companionData.children,
            custom_babies: companionData.babies,
            custom_pets: companionData.pets,
            custom_location: destination,
            custom_totalprice: priceStructure.price,
            checkin_date: dateSelection.start ?? '',
            checkout_date: dateSelection.end ?? ''
        };

        if (data.products && data.products[0]?.id) {
            data.products[0].item_id = data.products[0].id;
        }

        GoogleAnalyticsEvents.trackEvent<GoogleAnalytics4ViewContentEvent>('ViewContent', data);
    }

    public trackDetailPageImpression(dateSelection: DateRangeString, quantity: number = 1): void {
        let houseData: HouseDetailDataLayerData | undefined;
        const dataLayerElement = document.getElementById('data-layer');

        if (dataLayerElement && dataLayerElement.hasAttribute('content')) {
            const dataLayerContent = dataLayerElement.getAttribute('content');
            if (!dataLayerContent) {
                return;
            }

            houseData = JSON.parse(dataLayerContent);
        }

        const house = houseData?.products?.[0];

        if (!houseData || !house) {
            return;
        }

        const priceStructure = this.#getPriceStructure();

        if (isFeatureEnabled(FeatureToggle.GOOGLE_TAG_MANAGER)) {
            GTM.track(
                GTMViewItemModel.parseToGTMData({
                    isDateSelected: Boolean(dateSelection.start && dateSelection.end),
                    value: priceStructure.price,
                    id: house.id,
                    title: house.name,
                    country: houseData.custom_lastcountry,
                    region: houseData.custom_lastregion,
                    department: houseData.custom_lastdepartment,
                    place: houseData.custom_lastcity,
                    discount: priceStructure.discount,
                    pricePerNight: priceStructure.pricePerNight,
                    instantBooking: DetailFormTracking.getInstantBookingValue(),
                    quantity
                })
            );
            return;
        }

        GoogleAnalyticsEvents.productDetailImpression(house, priceStructure.price);
    }

    public processAddToCartData(
        dataLayer: Record<string, any>,
        quantity: number,
        dateSelection: DateRangeString
    ): void {
        if (!dateSelection.start || !dateSelection.end) {
            return;
        }

        const priceStructure = this.#getPriceStructure();

        if (isFeatureEnabled(FeatureToggle.GOOGLE_TAG_MANAGER)) {
            GTM.trackEcommerce(
                GTMDetailAddToCartButtonClickModel.parseToGTMData({
                    isDateSelected: true,
                    value: priceStructure.price,
                    id: dataLayer.products[0].id,
                    title: dataLayer.products[0].name,
                    country: dataLayer.custom_lastcountry,
                    region: dataLayer.custom_lastregion,
                    department: dataLayer.custom_lastdepartment,
                    place: dataLayer.custom_lastcity,
                    pricePerNight: priceStructure.pricePerNight,
                    discount: priceStructure.discount,
                    instantBooking: DetailFormTracking.getInstantBookingValue(),
                    quantity
                })
            );
        } else {
            GoogleAnalyticsEvents.addToCart(
                dataLayer,
                priceStructure.price,
                priceStructure.price ? Number(priceStructure.price / quantity).toFixed(2) : ''
            );
        }
    }

    #getPriceStructure(): PriceStructure {
        const defaultPriceStructure: PriceStructure = {
            price: 0,
            basePrice: 0,
            discount: 0,
            pricePerNight: 0
        };

        const priceStructureElement: HTMLElement | null =
            this.#formElement.querySelector('[data-price-structure]');

        try {
            if (!priceStructureElement?.dataset?.priceStructure) {
                return defaultPriceStructure;
            }

            return JSON.parse(priceStructureElement.dataset.priceStructure) as PriceStructure;
        } catch {
            return defaultPriceStructure;
        }
    }
}
